import React, { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Card, Divider, Dropdown, Menu, Modal, Popconfirm, Select, message } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import HyperLink, { ENTITY_TYPE, HYPERLINK_VARIANT } from '../common/HyperLink';
import {
	dispatchersRestCrudThunksForSupplier,
	dispatchersRestCrudThunksForSupplierBulk,
} from '../../thunks/dispatchers_thunks';
import useMountEffect from '../../hooks/useMountEffect';
import OWFlatfileImporter from '../ow_flatfile/OWFlatfileImporter';
import EditProblemTypesModal from './EditProblemTypesModal';
import ProblemTypeHierarchicalDisplay from '../problem_type_hierarchical_display/ProblemTypeHierarchicalDisplay';

const SUPPLIER_DISPATCHERS_TARGET_COLLECTION = 'fieldTechLocationsRoutingSupplierDispatchers';

const PRIMARY_TECH_ROLE = 'Primary Tech';
const CC_ROLE = 'CC';
const ASSIGNABLE_ROLES = [PRIMARY_TECH_ROLE, CC_ROLE];

const FieldTechLocationsRoutingCard: FC<any> = ({
	fieldTech,
	onFieldTechUpdated,
	fetchSupplierDispatchers,
	bulkUpdateDispatchers,
}): React.ReactElement => {
	const [dispatcherRecords, setDispatcherRecords] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [removingLocationId, setRemovingLocationId] = useState(null);
	const [updatingRoleLocationId, setUpdatingRoleLocationId] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showEditProblemTypes, setShowEditProblemTypes] = useState(false);

	const email = nullSafeGet('email', fieldTech);
	const problemTypeIds = nullSafeGetOrElse('problemTypes', fieldTech, []).map(
		(problemType) => problemType.id
	);
	const hasAccessToAllLocations = nullSafeGet('hasAccessToAllLocations', fieldTech);
	// When the tech has access to every location the backend leaves fieldTech.locations empty, so
	// derive the routable locations (with names) straight from the hydrated dispatcher records —
	// they already carry a full `location`, so no separate locations fetch is needed. Otherwise use
	// the tech's explicit accessible set.
	const accessibleLocations = useMemo(() => {
		if (!hasAccessToAllLocations) {
			return nullSafeGetOrElse('locations', fieldTech, []);
		}
		const locationById = {};
		dispatcherRecords.forEach((record) => {
			const locationId = nullSafeGet('locationId', record);
			if (locationId != null && !locationById[locationId]) {
				locationById[locationId] = {
					id: locationId,
					name: nullSafeGetOrElse('location.name', record, ''),
				};
			}
		});
		return Object.values(locationById);
	}, [hasAccessToAllLocations, fieldTech, dispatcherRecords]);

	// A field tech's email may live on the record directly or on the nested contact,
	// and the dispatcher lists key off email — check both so presence matching is reliable.
	const techEmails = useMemo(
		() =>
			[nullSafeGet('email', fieldTech), nullSafeGet('supplierContact.email', fieldTech)].filter(
				Boolean
			),
		[fieldTech]
	);

	// Fetch the supplier's dispatcher records; each carries the primaryTechList/ccList we match
	// this tech against, keyed by locationId. Re-run after a change to reflect the update.
	const loadDispatchers = () =>
		fetchSupplierDispatchers({}, SUPPLIER_DISPATCHERS_TARGET_COLLECTION, {
			current: 1,
			pageSize: 10000,
		})
			.then((records) => setDispatcherRecords(records || []))
			.catch(() => setDispatcherRecords([]));

	useMountEffect(() => {
		loadDispatchers().finally(() => setLoading(false));
	});

	const dispatcherByLocationId = useMemo(() => {
		const map = {};
		dispatcherRecords.forEach((record) => {
			map[record.locationId] = record;
		});
		return map;
	}, [dispatcherRecords]);

	// Primary Tech takes precedence over CC; any other category is not surfaced here.
	const getRoleForLocation = (locationId) => {
		const record = dispatcherByLocationId[locationId];
		if (!record) return null;
		const primaryTechList = nullSafeGetOrElse('primaryTechList', record, []);
		const ccList = nullSafeGetOrElse('ccList', record, []);
		if (primaryTechList.some((listEmail) => techEmails.includes(listEmail))) {
			return PRIMARY_TECH_ROLE;
		}
		if (ccList.some((listEmail) => techEmails.includes(listEmail))) {
			return CC_ROLE;
		}
		return null;
	};

	// The primary tech is a single-holder role, so whoever currently holds it (anyone other than
	// this tech) is what an assignment would replace. Prefer their contact name, fall back to email.
	const getPrimaryTechsToReplace = (locationId) => {
		const record = dispatcherByLocationId[locationId];
		if (!record) return [];
		const primaryTechList = nullSafeGetOrElse('primaryTechList', record, []);
		const contacts = nullSafeGetOrElse('primaryTechListContacts', record, []);
		return primaryTechList
			.filter((listEmail) => !techEmails.includes(listEmail))
			.map((listEmail) => {
				const contact = contacts.find((entry) => nullSafeGet('email', entry) === listEmail);
				const name = [nullSafeGet('nameGiven', contact), nullSafeGet('nameFamily', contact)]
					.filter(Boolean)
					.join(' ')
					.trim();
				return name || listEmail;
			});
	};

	// Build the bulk-update payload to set this tech's role at one location. Primary tech is
	// single-holder, so assigning it replaces the current holder; CCs are additive, so we only add
	// this tech and leave others alone. A null role clears the tech from both lists.
	const buildRolePayload = (locationId, newRole) => {
		const assigningPrimaryTech = newRole === PRIMARY_TECH_ROLE;
		const assigningCc = newRole === CC_ROLE;
		const existingPrimaryTechEmails = nullSafeGetOrElse(
			'primaryTechList',
			dispatcherByLocationId[locationId],
			[]
		).filter((listEmail) => !techEmails.includes(listEmail));
		return {
			locationIds: [locationId],
			addPrimaryTechList: assigningPrimaryTech ? [email] : [],
			removePrimaryTechList: assigningPrimaryTech ? existingPrimaryTechEmails : techEmails,
			addCcList: assigningCc ? [email] : [],
			removeCcList: assigningCc ? [] : techEmails,
		};
	};

	// Reassign the tech's role at a single location (used by the per-row role picker).
	const changeRole = (locationId, newRole) => {
		if (!email) return;
		setUpdatingRoleLocationId(locationId);
		bulkUpdateDispatchers(buildRolePayload(locationId, newRole))
			.then(() => loadDispatchers())
			.catch(() => message.error('Unable to update role for this location.'))
			.finally(() => setUpdatingRoleLocationId(null));
	};

	// Map location id -> name for the routing grid's read-only Location column and warnings.
	const locationNameById = useMemo(() => {
		const map = {};
		accessibleLocations.forEach((location) => {
			map[location.id] = location.name;
		});
		return map;
	}, [accessibleLocations]);

	// Grid columns: Location is read-only (shows the name, carries the id so we can map rows back).
	// Role uses a custom Select cell so "Not Routed" (no role) can render in gray — the standard
	// enum cell stringifies its label and can't style a single option.
	const routingGridFields = [
		{
			label: 'Location',
			key: 'locationId',
			type: 'string',
			renderCustomCell: ({ value }) => locationNameById[value] || value,
		},
		{
			label: 'Role',
			key: 'role',
			type: 'string',
			renderCustomCell: ({ value, onChange }) => (
				<Select
					bordered={false}
					className="w-full"
					value={value || ''}
					onChange={onChange}
					dropdownStyle={{ zIndex: 2000 }}
				>
					<Select.Option value="">
						<span className="text-grey-7">Not Routed</span>
					</Select.Option>
					<Select.Option value={PRIMARY_TECH_ROLE}>{PRIMARY_TECH_ROLE}</Select.Option>
					<Select.Option value={CC_ROLE}>{CC_ROLE}</Select.Option>
				</Select>
			),
		},
	];

	// One row per accessible location, seeded with the tech's current role there.
	const routingGridData = accessibleLocations.map((location) => ({
		locationId: location.id,
		role: getRoleForLocation(location.id) || '',
	}));

	// Persist the grid's edits: update every location whose role changed, then refresh. If any
	// change would displace an existing primary tech, warn first. Resolving lets the importer show
	// success and close; rejecting with no message on cancel keeps it open with no error toast.
	const handleRoutingGridSave = ({ validData }) =>
		new Promise((resolve, reject) => {
			if (!email) {
				resolve('No changes to save.');
				return;
			}
			const accessibleIds = new Set(accessibleLocations.map((location) => location.id));
			const changedRows = validData.filter((row) => {
				if (!accessibleIds.has(row.locationId)) return false;
				const currentRole = getRoleForLocation(row.locationId) || null;
				return currentRole !== (row.role || null);
			});
			if (changedRows.length === 0) {
				resolve('No changes to save.');
				return;
			}

			const applyChanges = () =>
				Promise.all(
					changedRows.map((row) =>
						bulkUpdateDispatchers(buildRolePayload(row.locationId, row.role || null))
					)
				)
					.then(() => loadDispatchers())
					.then(() => resolve('Work order routing updated.'))
					.catch(() => reject('Unable to update work order routing.'));

			const replacements = changedRows
				.filter(
					(row) =>
						row.role === PRIMARY_TECH_ROLE && getPrimaryTechsToReplace(row.locationId).length > 0
				)
				.map((row) => ({
					locationName: locationNameById[row.locationId],
					replacing: getPrimaryTechsToReplace(row.locationId),
				}));

			if (replacements.length > 0) {
				Modal.confirm({
					title: 'Replace existing primary tech?',
					content: (
						<div>
							{replacements.map((replacement) => (
								<div key={replacement.locationName}>
									{`${replacement.locationName}: replacing ${replacement.replacing.join(', ')}`}
								</div>
							))}
						</div>
					),
					okText: 'Replace',
					onOk: applyChanges,
					// Reject with no message: the importer treats a falsy rejection as a silent cancel,
					// so the grid stays open without an error toast.
					onCancel: () => reject(),
				});
				return;
			}
			applyChanges();
		});

	// Warn before replacing the current primary tech; CC assignment is additive, so no prompt.
	const handleSelectRole = (location, newRole) => {
		if (newRole === PRIMARY_TECH_ROLE) {
			const replacing = getPrimaryTechsToReplace(location.id);
			if (replacing.length > 0) {
				Modal.confirm({
					title: 'Replace primary tech?',
					content: `This will replace ${replacing.join(', ')} as the primary tech at ${
						location.name
					}.`,
					okText: 'Replace',
					onOk: () => changeRole(location.id, newRole),
				});
				return;
			}
		}
		changeRole(location.id, newRole);
	};

	// Remove the tech from this location's routing entirely (both primary tech and CC).
	const handleRemoveRouting = (locationId) => {
		setRemovingLocationId(locationId);
		bulkUpdateDispatchers(buildRolePayload(locationId, null))
			.then(() => loadDispatchers())
			.catch(() => message.error('Unable to remove routing for this location.'))
			.finally(() => setRemovingLocationId(null));
	};

	const renderLocationRow = (location) => {
		const role = getRoleForLocation(location.id);
		return (
			<div
				key={`routing-location-${location.id}`}
				className="flex items-center justify-between gap-2 py-1"
			>
				<div className="min-w-0 flex-1 truncate">
					<HyperLink
						text={location.name}
						entityType={ENTITY_TYPE.LOCATION}
						entityId={location.id}
						backLinkText={'Back to Field Tech Detail'}
						variant={HYPERLINK_VARIANT.SUBTLE}
					/>
				</div>
				<div className="flex shrink-0 items-center gap-2">
					{role && (
						<span className="footnoteregular whitespace-nowrap text-character-secondary">
							{role}
						</span>
					)}
					<AccessPermissionChecker
						name={PERMISSION_NAMES.ASSIGN_WORK_ORDER_ROUTING}
						permissionDeniedContent={null}
					>
						<Dropdown
							trigger={['click']}
							overlay={
								<Menu
									selectedKeys={role ? [role] : []}
									onClick={({ key }) => handleSelectRole(location, key)}
								>
									{ASSIGNABLE_ROLES.map((assignableRole) => (
										<Menu.Item key={assignableRole}>{assignableRole}</Menu.Item>
									))}
								</Menu>
							}
						>
							<Button
								type="text"
								size="small"
								icon={<EditOutlined />}
								loading={updatingRoleLocationId === location.id}
							/>
						</Dropdown>
						{role && (
							<Popconfirm
								title="Remove this field tech from the location's routing?"
								okText="Remove"
								cancelText="Cancel"
								onConfirm={() => handleRemoveRouting(location.id)}
							>
								<Button
									type="text"
									size="small"
									icon={<DeleteOutlined />}
									loading={removingLocationId === location.id}
								/>
							</Popconfirm>
						)}
					</AccessPermissionChecker>
				</div>
			</div>
		);
	};

	const renderLocationRoutingBody = () => {
		// Only surface locations where this tech actually holds a routing role (Primary Tech or CC).
		const routingDesignatedLocations = accessibleLocations.filter((location) =>
			getRoleForLocation(location.id)
		);

		if (routingDesignatedLocations.length === 0) {
			return 'No locations with routing designated';
		}

		return <div>{routingDesignatedLocations.map(renderLocationRow)}</div>;
	};

	const renderSubsectionHeader = (title, permissionName, onEdit) => (
		<div className="mb-2 flex items-center justify-between">
			<span className="h5medium">{title}</span>
			<AccessPermissionChecker name={permissionName} permissionDeniedContent={null}>
				<Button type="link" className="px-0" onClick={onEdit}>
					Edit
				</Button>
			</AccessPermissionChecker>
		</div>
	);

	return (
		<div className="rowSpacing">
			<Card title="Work Order Routing" loading={loading}>
				{renderSubsectionHeader(
					'Location Routing',
					PERMISSION_NAMES.ASSIGN_WORK_ORDER_ROUTING,
					() => setShowEditModal(true)
				)}
				{renderLocationRoutingBody()}

				<Divider />

				{renderSubsectionHeader('Problem Type Routing', PERMISSION_NAMES.ASSIGN_PROBLEM_TYPES, () =>
					setShowEditProblemTypes(true)
				)}
				<ProblemTypeHierarchicalDisplay problemTypeIds={problemTypeIds} />
			</Card>
			{showEditModal && (
				<OWFlatfileImporter
					dataLoadComplete={showEditModal}
					type="work_order_routing"
					entityName="Location Routing"
					fields={routingGridFields}
					data={routingGridData}
					allowAddRows={false}
					allowDeleteRows={false}
					onComplete={() => setShowEditModal(false)}
					onDataLoad={handleRoutingGridSave}
				/>
			)}
			{showEditProblemTypes && (
				<EditProblemTypesModal
					email={email}
					problemTypeIds={problemTypeIds}
					onCancel={() => setShowEditProblemTypes(false)}
					onSuccess={() => {
						setShowEditProblemTypes(false);
						onFieldTechUpdated && onFieldTechUpdated();
					}}
				/>
			)}
		</div>
	);
};

const mapDispatchToProps = (dispatch) => ({
	fetchSupplierDispatchers: (params, targetCollectionName, pagination) =>
		dispatch(dispatchersRestCrudThunksForSupplier.read(params, targetCollectionName, pagination)),
	bulkUpdateDispatchers: (payload) =>
		dispatch(dispatchersRestCrudThunksForSupplierBulk.update(payload)),
});

export default connect(null, mapDispatchToProps)(FieldTechLocationsRoutingCard);
